<template>
  <div v-if="visible" class="cookie-root">
    <!-- Banner -->
    <div v-if="!showPanel" class="cookie-banner" role="dialog" aria-live="polite"
      aria-label="Cookie notice">
      <div class="cookie-body">
        <h2 class="cookie-title">We value your privacy</h2>
        <p class="cookie-text">
          We use essential cookies to make this site work. With your permission
          we would also use optional cookies for analytics and to load the map
          of our office. Read our
          <router-link to="/cookie-policy">Cookie Policy</router-link> and
          <router-link to="/privacy-policy">Privacy Policy</router-link>.
        </p>
      </div>

      <div class="cookie-actions">
        <button type="button" class="btn btn-accept" @click="onAcceptAll">
          Accept all
        </button>
        <button type="button" class="btn btn-reject" @click="onRejectAll">
          Reject optional
        </button>
        <button type="button" class="btn btn-link" @click="showPanel = true">
          Manage preferences
        </button>
      </div>
    </div>

    <!-- Preferences panel -->
    <div v-else class="cookie-overlay" @click.self="closePanel">
      <div class="cookie-panel" role="dialog" aria-modal="true"
        aria-label="Cookie preferences">
        <div class="panel-head">
          <h2 class="cookie-title">Cookie preferences</h2>
          <button type="button" class="panel-close" aria-label="Close"
            @click="closePanel">
            &times;
          </button>
        </div>

        <p class="cookie-text">
          Choose which cookies you allow. You can change this at any time from
          the Cookie settings link in the footer.
        </p>

        <div class="category-list">
          <div v-for="cat in categoryList" :key="cat.key" class="category">
            <div class="category-head">
              <span class="category-label">{{ cat.label }}</span>

              <span v-if="cat.readOnly" class="category-fixed">
                Always active
              </span>
              <label v-else class="switch">
                <input type="checkbox" v-model="draft[cat.key]" />
                <span class="slider"></span>
                <span class="sr-only">Allow {{ cat.label }} cookies</span>
              </label>
            </div>
            <p class="category-desc">{{ cat.description }}</p>
          </div>
        </div>

        <div class="cookie-actions panel-actions">
          <button type="button" class="btn btn-accept" @click="onSave">
            Save preferences
          </button>
          <button type="button" class="btn btn-reject" @click="onAcceptAll">
            Accept all
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue';
import {
  consent,
  CATEGORIES,
  saveConsent,
  acceptAll,
  rejectOptional,
} from '@/utils/consent';

const visible = ref(false);
const showPanel = ref(false);

const draft = reactive({
  analytics: consent.analytics,
  embeds: consent.embeds,
});

const categoryList = computed(() => Object.values(CATEGORIES));

const syncDraft = () => {
  draft.analytics = consent.analytics;
  draft.embeds = consent.embeds;
};

const closePanel = () => {
  showPanel.value = false;
  // If the visitor has never decided, fall back to the banner rather than
  // dismissing the notice entirely.
  visible.value = !consent.decided;
};

const onAcceptAll = () => {
  acceptAll();
  visible.value = false;
  showPanel.value = false;
};

const onRejectAll = () => {
  rejectOptional();
  visible.value = false;
};

const onSave = () => {
  saveConsent({ analytics: draft.analytics, embeds: draft.embeds });
  visible.value = false;
  showPanel.value = false;
};

const openPreferences = () => {
  syncDraft();
  showPanel.value = true;
  visible.value = true;
};

onMounted(() => {
  visible.value = !consent.decided;
  syncDraft();
  window.addEventListener('cookie-consent-open', openPreferences);
});

onBeforeUnmount(() => {
  window.removeEventListener('cookie-consent-open', openPreferences);
});
</script>

<style scoped>
.cookie-banner {
  position: fixed;
  left: 20px;
  right: 20px;
  bottom: 20px;
  z-index: 9999;
  max-width: 640px;
  margin: 0 auto;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-top: 4px solid #b91c1c;
  border-radius: 10px;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.22);
  padding: 24px;
  font-family: system-ui, -apple-system, sans-serif;
  color: #1a1a1a;
  animation: cookieIn 0.4s ease forwards;
}

@keyframes cookieIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.cookie-title {
  font-size: 1.05rem;
  font-weight: 800;
  margin: 0 0 8px;
  color: #111;
}

.cookie-text {
  font-size: 0.9rem;
  line-height: 1.6;
  color: #4a5568;
  margin: 0 0 18px;
}

.cookie-text a {
  color: #b91c1c;
  font-weight: 600;
  text-decoration: underline;
}

.cookie-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.btn {
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 700;
  padding: 11px 20px;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s ease;
}

.btn-accept {
  background: #b91c1c;
  color: #fff;
  border-color: #b91c1c;
}

.btn-accept:hover {
  background: #911515;
  border-color: #911515;
}

.btn-reject {
  background: #fff;
  color: #1a1a1a;
  border-color: #cbd5e1;
}

.btn-reject:hover {
  border-color: #b91c1c;
  color: #b91c1c;
}

.btn-link {
  background: none;
  border: none;
  color: #64748b;
  text-decoration: underline;
  padding: 11px 6px;
}

.btn-link:hover {
  color: #b91c1c;
}

/* Preferences panel */
.cookie-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.cookie-panel {
  background: #fff;
  border-radius: 12px;
  max-width: 560px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  padding: 28px;
  font-family: system-ui, -apple-system, sans-serif;
  color: #1a1a1a;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.3);
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.panel-close {
  background: none;
  border: none;
  font-size: 1.6rem;
  line-height: 1;
  color: #94a3b8;
  cursor: pointer;
  padding: 0 4px;
}

.panel-close:hover {
  color: #b91c1c;
}

.category-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 8px 0 24px;
}

.category {
  border: 1px solid #f1f5f9;
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px 18px;
}

.category-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.category-label {
  font-weight: 700;
  font-size: 0.95rem;
}

.category-fixed {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #b91c1c;
}

.category-desc {
  margin: 8px 0 0;
  font-size: 0.85rem;
  line-height: 1.55;
  color: #64748b;
}

.panel-actions {
  justify-content: flex-start;
}

/* Toggle */
.switch {
  position: relative;
  display: inline-block;
  width: 46px;
  height: 26px;
  flex-shrink: 0;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #cbd5e1;
  border-radius: 26px;
  transition: 0.25s;
}

.slider::before {
  content: '';
  position: absolute;
  height: 20px;
  width: 20px;
  left: 3px;
  bottom: 3px;
  background: #fff;
  border-radius: 50%;
  transition: 0.25s;
}

.switch input:checked + .slider {
  background: #b91c1c;
}

.switch input:checked + .slider::before {
  transform: translateX(20px);
}

.switch input:focus-visible + .slider {
  outline: 2px solid #b91c1c;
  outline-offset: 2px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 520px) {
  .cookie-banner {
    left: 12px;
    right: 12px;
    bottom: 12px;
    padding: 20px;
  }

  .cookie-actions .btn-accept,
  .cookie-actions .btn-reject {
    flex: 1 1 100%;
  }
}
</style>
